import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import Toast from './Toast';
import PanelBreadcrumbs from './PanelBreadcrumbs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Chat = ({ setActivePage }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`chat-${Date.now()}`);
  const [pinnedReports, setPinnedReports] = useState([]);
  const [showPinnedReports, setShowPinnedReports] = useState(false);
  const [toast, setToast] = useState(null);
  // New pagination state
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageCursor, setPageCursor] = useState(null);
  const [historyPaginationMode, setHistoryPaginationMode] = useState('cursor');
  const [showLoadMoreHeader, setShowLoadMoreHeader] = useState(false);
  const [draggedMessage, setDraggedMessage] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [messageViewMode, setMessageViewMode] = useState({}); // Track view mode for each message: 'chart' or 'table'
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sentinelRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isLoadingOlderMessagesRef = useRef(false);
  const hasScrolledToBottomRef = useRef(false);
  const lastMessageCountRef = useRef(0);
  const scrollTimeoutRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const observerTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    // Clear any pending scroll operations
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }

    // Only scroll to bottom if we're not loading older messages
    if (!isLoadingOlderMessagesRef.current) {
      const messagesContainer = messagesContainerRef.current;
      const currentMessageCount = messages.length;
      const messageCountChanged = currentMessageCount !== lastMessageCountRef.current;
      
      if (messagesContainer && currentMessageCount > 0) {
        // On initial load, always scroll to bottom immediately
        if (isInitialLoadRef.current) {
          // Force scroll to bottom on initial load
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
          hasScrolledToBottomRef.current = true;
          isInitialLoadRef.current = false;
          lastMessageCountRef.current = currentMessageCount;
          return;
        }
        
        // After initial load, only scroll if message count changed and we're near bottom
        if (messageCountChanged) {
          const scrollHeight = messagesContainer.scrollHeight;
          const scrollTop = messagesContainer.scrollTop;
          const clientHeight = messagesContainer.clientHeight;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          const isNearBottom = distanceFromBottom < 200;
          
          // Scroll to bottom only if new message added and we're already near the bottom
          if (isNearBottom && currentMessageCount > lastMessageCountRef.current) {
            scrollTimeoutRef.current = setTimeout(() => {
              if (messagesContainer && !isLoadingOlderMessagesRef.current) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
                hasScrolledToBottomRef.current = true;
              }
              scrollTimeoutRef.current = null;
            }, 50);
          }
          
          lastMessageCountRef.current = currentMessageCount;
        }
      }
    }

    // Cleanup function
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [messages, scrollToBottom]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMsg = { role: 'user', content: input, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    setInput('');
  
    try {
      // Get auth token from localStorage
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/api/chat`,
        {
          message: input.trim()
        },
        {
          timeout: 360000,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'accept': 'application/json'
          }
        }
      );
      console.log('API Response:', response.data);
  
      const apiData = response.data;
      let parsedMessage = '';
      let dataTable = null;
      let summary = null;
      let visualization = null;
      let csvFileName = null;
      let chatMessageId = null;

      // Extract chat_message_id from response
      if (apiData.chat_message_id !== undefined) {
        chatMessageId = apiData.chat_message_id;
      } else if (apiData.id !== undefined) {
        chatMessageId = apiData.id;
      }

      let responseData = apiData.output || apiData;
      
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (e) {
          parsedMessage = responseData;
        }
      }
      
      if (typeof responseData === 'object' && responseData !== null) {
        if (responseData.message && typeof responseData.message === 'string') {
          parsedMessage = responseData.message;
        } else {
          parsedMessage = JSON.stringify(responseData, null, 2);
        }
        
        if (responseData.data && Array.isArray(responseData.data)) {
          dataTable = responseData.data;
        }
        
        if (responseData.summary && typeof responseData.summary === 'object') {
          summary = responseData.summary;
        }
        
        if (responseData.visualization && typeof responseData.visualization === 'object') {
          visualization = responseData.visualization;
        }
        
        if (responseData.csv_file_name && typeof responseData.csv_file_name === 'string') {
          csvFileName = responseData.csv_file_name;
        }
      } else {
        parsedMessage = String(responseData || 'No response received');
      }
  
      const contentString = typeof parsedMessage === 'string' 
        ? parsedMessage 
        : JSON.stringify(parsedMessage, null, 2);

      const aiMsg = {
        role: 'ai',
        content: contentString,
        dataTable,
        summary,
        visualization,
        csvFileName,
        chatMessageId,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        setLoading(false);
        return;
      }
      
      console.error('API Error:', error);
      let errorMessage = 'Sorry, I encountered an error while processing your request.';
      
      if (error.response) {
        errorMessage = `Error ${error.response.status}: ${error.response.data?.message || error.response.statusText || 'Server error'}`;
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection and try again.';
      } else {
        errorMessage = `Error: ${error.message}`;
      }
      
      const errorMsg = {
        role: 'ai',
        content: errorMessage,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Fetch pinned reports
  const fetchPinnedReports = async () => {
    try {
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      const response = await axios.get(`${API_BASE_URL}/api/pinned-reports`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setPinnedReports(response.data.pinned_reports || response.data.data || response.data || []);
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error fetching pinned reports:', error);
    }
  };

  // Pin a report
  const pinReport = async (chatMessageId) => {
    if (!chatMessageId) {
      setToast({ message: 'Cannot pin: Message ID not available', type: 'error' });
      return;
    }

    try {
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      await axios.post(
        `${API_BASE_URL}/api/pinned-reports`,
        { chat_message_id: chatMessageId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setToast({ message: 'Report pinned successfully', type: 'success' });
      fetchPinnedReports(); // Refresh pinned reports list
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error pinning report:', error);
      setToast({ message: error.response?.data?.error || 'Failed to pin report', type: 'error' });
    }
  };

  // Handle drag start for pinning reports
  const handleDragStart = (e, message) => {
    if (message.chatMessageId && message.role === 'ai' && message.dataTable) {
      setDraggedMessage(message);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', message.chatMessageId.toString());
      // Add visual feedback
      e.currentTarget.style.opacity = '0.5';
    } else {
      e.preventDefault();
    }
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedMessage(null);
    setIsDraggingOver(false);
  };

  // Handle drag over drop zone
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDraggingOver(true);
  };

  // Handle drag leave drop zone
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  // Handle drop to pin report
  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    
    if (draggedMessage && draggedMessage.chatMessageId) {
      await pinReport(draggedMessage.chatMessageId);
      setDraggedMessage(null);
    }
  };

  // Unpin a report
  const unpinReport = async (pinnedReportId) => {
    try {
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      await axios.delete(`${API_BASE_URL}/api/pinned-reports/${pinnedReportId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setToast({ message: 'Report unpinned successfully', type: 'success' });
      fetchPinnedReports(); // Refresh pinned reports list
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error unpinning report:', error);
      setToast({ message: error.response?.data?.error || 'Failed to unpin report', type: 'error' });
    }
  };

  // View pinned report
  const viewPinnedReport = async (pinnedReport) => {
    try {
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      const response = await axios.get(
        `${API_BASE_URL}/api/pinned-reports/${pinnedReport.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const reportData = response.data;
      
      // Create a message from the pinned report data
      const pinnedMsg = {
        role: 'ai',
        content: reportData.report_title || reportData.description || 'Pinned Report',
        dataTable: reportData.data || [],
        summary: reportData.summary || null,
        visualization: reportData.visualization || null,
        csvFileName: reportData.csv_file_name || null,
        chatMessageId: pinnedReport.chat_message_id || pinnedReport.id,
        timestamp: new Date(pinnedReport.created_at || Date.now())
      };

      setMessages(prev => [...prev, pinnedMsg]);
      setShowPinnedReports(false);
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error viewing pinned report:', error);
      setToast({ message: error.response?.data?.error || 'Failed to load pinned report', type: 'error' });
    }
  };

  // Convert history item to message format
  const convertHistoryItemToMessage = (item) => {
    const messages = [];
    
    // Add user message if this item is a user message
    if (item.role === 'user' && item.message) {
      messages.push({
        role: 'user',
        content: item.message,
        chatMessageId: item.id,
        timestamp: new Date(item.created_at || Date.now())
      });
    }
    
    // Add AI response if available (can be in same item or separate item)
    // Process response if this item has a response field
    if (item.response && typeof item.response === 'object' && Object.keys(item.response).length > 0) {
      const responseData = item.response;
      let parsedMessage = '';
      let dataTable = null;
      let summary = null;
      let visualization = null;
      let csvFileName = null;

      // First, try to parse response.output if it's a JSON string
      let actualData = responseData;
      if (responseData.output) {
        if (typeof responseData.output === 'string') {
          try {
            actualData = JSON.parse(responseData.output);
          } catch (e) {
            console.error('Error parsing response.output:', e);
            actualData = responseData;
          }
        } else {
          actualData = responseData.output;
        }
      }

      // Extract all data from the response
      if (actualData && typeof actualData === 'object') {
        // Get message
        if (actualData.message && typeof actualData.message === 'string') {
          parsedMessage = actualData.message;
        }
        
        // Get data array
        if (actualData.data && Array.isArray(actualData.data)) {
          dataTable = actualData.data;
        }
        
        // Get summary
        if (actualData.summary && typeof actualData.summary === 'object') {
          summary = actualData.summary;
        }
        
        // Get visualization (handle both object and string)
        if (actualData.visualization) {
          if (typeof actualData.visualization === 'string') {
            try {
              visualization = JSON.parse(actualData.visualization);
            } catch (e) {
              console.error('Error parsing visualization:', e);
            }
          } else if (typeof actualData.visualization === 'object') {
            visualization = actualData.visualization;
          }
        }
        
        // Get CSV file name
        if (actualData.csv_file_name && typeof actualData.csv_file_name === 'string') {
          csvFileName = actualData.csv_file_name;
        }
        
        // If no message but we have data, create a default message
        if (!parsedMessage && dataTable) {
          parsedMessage = 'Data retrieved';
        }
      }

      // Always add AI message if response exists
      messages.push({
        role: 'ai',
        content: parsedMessage || 'Response',
        dataTable: dataTable,
        summary: summary,
        visualization: visualization,
        csvFileName: csvFileName,
        chatMessageId: item.id,
        timestamp: new Date(item.created_at || Date.now())
      });
    }
    
    return messages;
  };

  // Fetch chat history with cursor-based pagination
  const fetchChatHistory = async (cursor = null, append = false) => {
    try {
      setLoadingMore(true);
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;

      const params = {
        limit: 5  // Load only 5 messages at a time
      };
      
      // Use cursor- or page-based pagination depending on API response mode
      if (cursor) {
        if (historyPaginationMode === 'page') {
          params.page = cursor;
        } else {
          params.cursor = cursor;
        }
      } else {
        params.page = 1;
      }

      const response = await axios.get(`${API_BASE_URL}/api/chat/history`, {
        params: params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const apiResponse = response.data;
      
      // Handle response format: { messages: [...], nextCursor: "def456", hasMore: true }
      // or fallback to existing format: { data: [...], pagination: {...} }
      let historyData = [];
      if (apiResponse.messages) {
        historyData = apiResponse.messages;
        setHistoryPaginationMode('cursor');
        setPageCursor(apiResponse.nextCursor || null);
        setHasMore(apiResponse.hasMore !== false);
      } else if (apiResponse.data) {
        historyData = apiResponse.data;
        // Update pagination info - handle both cursor and page-based responses
        if (apiResponse.nextCursor !== undefined) {
          setHistoryPaginationMode('cursor');
          setPageCursor(apiResponse.nextCursor);
          setHasMore(apiResponse.hasMore !== false);
        } else if (apiResponse.pagination) {
          // Fallback to page-based pagination
          const pagination = apiResponse.pagination;
          setHistoryPaginationMode('page');
          setHasMore(pagination.page < pagination.totalPages);
          setPageCursor(pagination.page + 1);
        } else {
          setHistoryPaginationMode('cursor');
          setHasMore(false);
          setPageCursor(null);
        }
      } else {
        setHistoryPaginationMode('cursor');
        setHasMore(false);
        setPageCursor(null);
      }
      
      if (Array.isArray(historyData) && historyData.length > 0) {
        // Convert history items to message format
        const historyMessages = [];
        const seenMessageIds = new Set(); // Track message IDs to prevent duplicates
        
        historyData.forEach((item) => {
          const itemMessages = convertHistoryItemToMessage(item);
          itemMessages.forEach((msg) => {
            // Use chatMessageId + role + content hash to identify unique messages
            // This prevents duplicates even if same ID appears with different content
            const contentHash = typeof msg.content === 'string' 
              ? msg.content.substring(0, 50) 
              : String(msg.content).substring(0, 50);
            const messageId = msg.chatMessageId 
              ? `${msg.role}-${msg.chatMessageId}-${contentHash}`
              : `${msg.role}-${msg.timestamp?.getTime() || Date.now()}-${contentHash}`;
            
            // Only add if we haven't seen this message before
            if (!seenMessageIds.has(messageId)) {
              seenMessageIds.add(messageId);
              historyMessages.push(msg);
            }
          });
        });
        
        // If no new messages were added (all duplicates), set hasMore to false
        if (append && historyMessages.length === 0) {
          setHasMore(false);
          return;
        }
        
        // Sort messages by timestamp (oldest to newest) - ensures latest at bottom
        historyMessages.sort((a, b) => {
          const timeA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp || 0);
          const timeB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp || 0);
          return timeA.getTime() - timeB.getTime();
        });
        
        if (append) {
          // Prepend older messages (since we're loading older pages)
          // Combine and sort to maintain chronological order, removing duplicates
          setMessages(prev => {
            const prevMessageIds = new Set();
            prev.forEach((msg) => {
              const messageId = msg.chatMessageId 
                ? `${msg.role}-${msg.chatMessageId}`
                : `${msg.role}-${msg.timestamp?.getTime() || 0}`;
              prevMessageIds.add(messageId);
            });
            
            // Filter out duplicates from new messages
            const uniqueNewMessages = historyMessages.filter((msg) => {
              const messageId = msg.chatMessageId 
                ? `${msg.role}-${msg.chatMessageId}`
                : `${msg.role}-${msg.timestamp?.getTime() || 0}`;
              return !prevMessageIds.has(messageId);
            });
            
            // If no unique new messages, set hasMore to false
            if (uniqueNewMessages.length === 0) {
              setHasMore(false);
              return prev;
            }
            
            const combined = [...uniqueNewMessages, ...prev];
            combined.sort((a, b) => {
              const timeA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp || 0);
              const timeB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp || 0);
              return timeA.getTime() - timeB.getTime();
            });
            return combined;
          });
        } else {
          // Replace messages (for initial load) - already sorted oldest to newest
          setMessages(historyMessages);
          
          // Scroll to bottom after initial messages are set
          setTimeout(() => {
            const messagesContainer = messagesContainerRef.current;
            if (messagesContainer && isInitialLoadRef.current) {
              messagesContainer.scrollTop = messagesContainer.scrollHeight;
              hasScrolledToBottomRef.current = true;
              isInitialLoadRef.current = false;
            }
          }, 100);
        }
      } else {
        // If no history data returned, set hasMore to false
        setHasMore(false);
        if (!append) {
          // If no history and not appending, clear messages
          setMessages([]);
        }
      }
    } catch (error) {
      if (error.isSessionExpired || (error.response && error.response.status === 401)) {
        return;
      }
      console.error('Error fetching chat history:', error);
      setHasMore(false);
      // Don't show error toast for history - it's okay if there's no history
    } finally {
      setLoadingMore(false);
    }
  };

  // Load more chat history (older messages)
  const loadMoreHistory = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (!hasMore || loadingMore || isLoadingMoreRef.current) {
      return;
    }
    
    // Set loading flags immediately to prevent duplicate calls
    isLoadingMoreRef.current = true;
    setLoadingMore(true);
    
    // Store current scroll position
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) {
      isLoadingMoreRef.current = false;
      setLoadingMore(false);
      return;
    }
    
    const scrollHeight = messagesContainer.scrollHeight;
    const scrollTop = messagesContainer.scrollTop;
    
    // Set flag to prevent auto-scroll to bottom
    isLoadingOlderMessagesRef.current = true;
    
    try {
      await fetchChatHistory(pageCursor, true); // Append older messages (prepends to array)
      
      // Restore scroll position after loading
      // When we prepend messages, the scroll height increases
      // We need to adjust scrollTop by the height difference to maintain the same view
      setTimeout(() => {
        if (messagesContainer) {
          const newScrollHeight = messagesContainer.scrollHeight;
          const heightAdded = newScrollHeight - scrollHeight;
          
          // Adjust scroll position: add the height that was added above the current view
          messagesContainer.scrollTop = scrollTop + heightAdded;
        }
        // Reset flag after scroll position is restored
        isLoadingOlderMessagesRef.current = false;
      }, 200);
    } finally {
      // Always reset loading flag
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, loadingMore, pageCursor, fetchChatHistory]);

  // Load chat history and pinned reports on mount
  useEffect(() => {
    fetchChatHistory(null, false); // Load first page
    fetchPinnedReports();
  }, []);

  // Intersection Observer for auto-loading when scrolling to top
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const messagesContainer = messagesContainerRef.current;
    if (!sentinel || !messagesContainer) return;

    // Don't set up observer until initial load is complete
    if (isInitialLoadRef.current) {
      return;
    }

    // If there are no more messages, don't set up observer
    if (!hasMore) {
      return;
    }

    // Clear any pending observer timeout
    if (observerTimeoutRef.current) {
      clearTimeout(observerTimeoutRef.current);
      observerTimeoutRef.current = null;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        
        // Only trigger if:
        // 1. Sentinel is intersecting (user scrolled to top)
        // 2. There are more messages to load
        // 3. Not currently loading
        // 4. Not on initial load
        // 5. No pending load operation
        if (
          entry.isIntersecting && 
          hasMore && 
          !loadingMore && 
          !isLoadingMoreRef.current &&
          !isInitialLoadRef.current
        ) {
          // Debounce: clear any existing timeout and set a new one
          if (observerTimeoutRef.current) {
            clearTimeout(observerTimeoutRef.current);
          }
          
          observerTimeoutRef.current = setTimeout(() => {
            // Double-check conditions before calling (state might have changed)
            if (hasMore && !loadingMore && !isLoadingMoreRef.current) {
              loadMoreHistory();
            }
            observerTimeoutRef.current = null;
          }, 300); // 300ms debounce to prevent rapid successive calls
        }
      },
      {
        root: messagesContainer,
        rootMargin: '50px', // Reduced from 100px to trigger closer to top
        threshold: 0.1
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (observerTimeoutRef.current) {
        clearTimeout(observerTimeoutRef.current);
        observerTimeoutRef.current = null;
      }
    };
  }, [hasMore, loadingMore, loadMoreHistory]);

  // Track scroll position to show/hide Load More header
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const scrollTop = messagesContainer.scrollTop;
      const scrollHeight = messagesContainer.scrollHeight;
      const clientHeight = messagesContainer.clientHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // Show header only when:
      // 1. Scrolled up more than 200px from top
      // 2. Not near the bottom (more than 100px from bottom) - so it doesn't show when viewing latest messages
      // 3. Has more messages to load
      const isScrolledUp = scrollTop > 200;
      const isNotNearBottom = distanceFromBottom > 100;
      
      setShowLoadMoreHeader(isScrolledUp && isNotNearBottom && hasMore);
    };

    messagesContainer.addEventListener('scroll', handleScroll);
    
    // Initial check - don't show on initial load
    setShowLoadMoreHeader(false);

    return () => {
      messagesContainer.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore]);

  // Check if we're viewing a pinned report from PinnedReports page
  useEffect(() => {
    const viewPinnedReportData = sessionStorage.getItem('viewPinnedReport');
    if (viewPinnedReportData) {
      try {
        const reportData = JSON.parse(viewPinnedReportData);
        
        // Ensure visualization is properly parsed
        let visualization = reportData.visualization || null;
        if (visualization && typeof visualization === 'string') {
          try {
            visualization = JSON.parse(visualization);
          } catch (e) {
            console.error('Error parsing visualization:', e);
            visualization = null;
          }
        }
        
        // Ensure dataTable is an array
        let dataTable = reportData.dataTable || [];
        if (!Array.isArray(dataTable)) {
          dataTable = [];
        }
        
        const pinnedMsg = {
          role: 'ai',
          content: reportData.content,
          dataTable: dataTable,
          summary: reportData.summary || null,
          visualization: visualization,
          csvFileName: reportData.csvFileName || null,
          chatMessageId: reportData.chatMessageId || null,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, pinnedMsg]);
        sessionStorage.removeItem('viewPinnedReport');
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      } catch (e) {
        console.error('Error parsing pinned report data:', e);
        sessionStorage.removeItem('viewPinnedReport');
      }
    }
  }, []);

  const convertToCSV = (data, visualization = null) => {
    if (!data || !Array.isArray(data) || data.length === 0) return '';
    
    const keys = Object.keys(data[0]);
    const orderedHeaders = [];
    if (visualization?.x_axis && keys.includes(visualization.x_axis)) {
      orderedHeaders.push(visualization.x_axis);
    }
    keys.forEach(key => {
      if (!orderedHeaders.includes(key)) {
        orderedHeaders.push(key);
      }
    });
    
    const headerRow = orderedHeaders.map(header => 
      `"${header.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}"`
    ).join(',');
    
    const dataRows = data.map(row => {
      return orderedHeaders.map(header => {
        const value = row[header];
        const stringValue = value === null || value === undefined ? '' : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      }).join(',');
    });
    
    return [headerRow, ...dataRows].join('\n');
  };

  const downloadCSV = (data, filename = 'data', visualization = null) => {
    const csvContent = convertToCSV(data, visualization);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const prepareChartData = (visualization, data) => {
    if (!visualization || !data || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    const { type, x_axis, y_axis, title, series, y_axis_left, y_axis_right } = visualization;

    const coerceNumber = (val) => {
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const cleaned = val.replace(/,/g, '').trim();
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    };

    // Dual-axis line chart: two metrics with separate y-axes
    if (type === 'dual_axis_line') {
      const labels = data.map((item) => formatChartLabel(item?.[x_axis]));
      const leftValues = data.map((item) => coerceNumber(item?.[y_axis_left]));
      const rightValues = data.map((item) => coerceNumber(item?.[y_axis_right]));

      return {
        labels,
        datasets: [
          {
            label: String(y_axis_left || 'Left Axis'),
            data: leftValues,
            borderColor: 'rgba(99, 102, 241, 1)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            yAxisID: 'yLeft',
          },
          {
            label: String(y_axis_right || 'Right Axis'),
            data: rightValues,
            borderColor: 'rgba(34, 197, 94, 1)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            yAxisID: 'yRight',
          },
        ],
      };
    }
    
    if (series && Array.isArray(series) && series.length > 0) {
      const labels = data.map(item => formatChartLabel(item[x_axis]));
      
      const backgroundColors = [
        'rgba(99, 102, 241, 0.7)',
        'rgba(139, 92, 246, 0.7)',
        'rgba(236, 72, 153, 0.7)',
        'rgba(251, 146, 60, 0.7)',
        'rgba(34, 197, 94, 0.7)',
        'rgba(59, 130, 246, 0.7)',
        'rgba(168, 85, 247, 0.7)',
        'rgba(245, 101, 101, 0.7)',
      ];
      
      const datasets = series.map((seriesName, index) => {
        const values = data.map(item => item[seriesName] || 0);
        
        return {
          label: String(seriesName),
          data: values,
          backgroundColor: (type === 'line' || type === 'group_line')
            ? backgroundColors[index % backgroundColors.length].replace('0.7', '0.1')
            : backgroundColors[index % backgroundColors.length],
          borderColor: backgroundColors[index % backgroundColors.length].replace('0.7', '1'),
          borderWidth: 2,
          tension: (type === 'line' || type === 'group_line') ? 0.4 : 0,
        };
      });
      
      return { labels, datasets };
    }
    
    if (type === 'group_line') {
      const rawXValues = [...new Set(
        data
          .map((item) => item?.[x_axis])
          .filter((value) => value !== undefined && value !== null)
      )];
      const labels = rawXValues.map((value) => formatChartLabel(value));
      const allKeys = Object.keys(data[0]);
      let categoryKey = (typeof series === 'string' && series.trim()) ? series.trim() : null;
      if (!categoryKey || !allKeys.includes(categoryKey)) {
        categoryKey = allKeys.find(key =>
          key.toLowerCase() === 'category' ||
          key.toLowerCase() === 'group'
        );
      }
      if (!categoryKey) {
        categoryKey = allKeys.find(key =>
          key !== x_axis &&
          key !== y_axis
        );
      }
      
      const categories = categoryKey 
        ? [...new Set(data.map(item => item[categoryKey]))]
        : [];
      
      if (!categoryKey || categories.length === 0) {
        const values = rawXValues.map((rawX) => {
          const matchingRows = data.filter((row) => String(row?.[x_axis]) === String(rawX));
          if (!matchingRows.length) return 0;
          return matchingRows.reduce((sum, row) => sum + coerceNumber(row?.[y_axis]), 0);
        });
        
        return {
          labels,
          datasets: [{
            label: y_axis.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            data: values,
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderColor: 'rgba(99, 102, 241, 1)',
            borderWidth: 2,
            tension: 0.4,
          }],
        };
      }
      
      const backgroundColors = [
        'rgba(99, 102, 241, 0.7)',
        'rgba(139, 92, 246, 0.7)',
        'rgba(236, 72, 153, 0.7)',
        'rgba(251, 146, 60, 0.7)',
        'rgba(34, 197, 94, 0.7)',
        'rgba(59, 130, 246, 0.7)',
        'rgba(168, 85, 247, 0.7)',
        'rgba(245, 101, 101, 0.7)',
      ];
      
      const datasets = categories.map((category, index) => {
        const values = rawXValues.map((rawX) => {
          const matchingRows = data.filter((row) =>
            String(row?.[x_axis]) === String(rawX) &&
            String(row?.[categoryKey]) === String(category)
          );
          if (!matchingRows.length) return 0;
          return matchingRows.reduce((sum, row) => sum + coerceNumber(row?.[y_axis]), 0);
        });
        
        return {
          label: String(category),
          data: values,
          backgroundColor: backgroundColors[index % backgroundColors.length].replace('0.7', '0.1'),
          borderColor: backgroundColors[index % backgroundColors.length].replace('0.7', '1'),
          borderWidth: 2,
          tension: 0.4,
        };
      });
      
      return { labels, datasets };
    } else if (type === 'line' || type === 'bar') {
      const labels = data.map(item => formatChartLabel(item[x_axis]));
      const values = data.map(item => item[y_axis]);
      
      return {
        labels,
        datasets: [{
          label: y_axis.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          data: values,
          backgroundColor: type === 'bar' 
            ? 'rgba(99, 102, 241, 0.5)'
            : 'rgba(99, 102, 241, 0.1)',
          borderColor: 'rgba(99, 102, 241, 1)',
          borderWidth: 2,
          tension: type === 'line' ? 0.4 : 0,
        }],
      };
    } else if (type === 'pie' || type === 'donut') {
      const labels = data.map(item => formatChartLabel(item[x_axis]));
      const values = data.map(item => item[y_axis]);
      
      const backgroundColors = [
        'rgba(99, 102, 241, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(236, 72, 153, 0.8)',
        'rgba(251, 146, 60, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(59, 130, 246, 0.8)',
      ];
      
      return {
        labels,
        datasets: [{
          label: y_axis.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          data: values,
          backgroundColor: backgroundColors.slice(0, values.length),
          borderColor: '#fff',
          borderWidth: 2,
        }],
      };
    }
    
    return null;
  };

  const renderChart = (visualization, data) => {
    const chartData = prepareChartData(visualization, data);
    if (!chartData) return null;

    const { type, title } = visualization;
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: !!title,
          text: title || '',
        },
      },
    };

    if (type === 'dual_axis_line') {
      const dualAxisOptions = {
        ...chartOptions,
        interaction: { mode: 'index', intersect: false },
        scales: {
          yLeft: {
            type: 'linear',
            position: 'left',
            grid: { drawOnChartArea: true },
          },
          yRight: {
            type: 'linear',
            position: 'right',
            grid: { drawOnChartArea: false },
          },
        },
      };
      return <Line data={chartData} options={dualAxisOptions} />;
    }

    if (type === 'line' || type === 'group_line') {
      return <Line data={chartData} options={chartOptions} />;
    } else if (type === 'bar') {
      return <Bar data={chartData} options={chartOptions} />;
    } else if (type === 'pie' || type === 'donut') {
      return <Pie data={chartData} options={chartOptions} />;
    }
    
    return null;
  };

  const getVisualizationIcon = (visualizationType) => {
    if (!visualizationType) return '📊';
    const type = visualizationType.toLowerCase();
    if (type === 'line' || type === 'group_line') return '📈';
    if (type === 'bar' || type === 'group_bar') return '📊';
    if (type === 'pie' || type === 'donut') return '🥧';
    if (type === 'table') return '📋';
    return '📊';
  };

  const formatHumanReadableDate = (dateValue) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return String(dateValue || '');
    }

    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  const isDateLikeValue = (value) => {
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }

    if (typeof value !== 'string') {
      return false;
    }

    const trimmed = value.trim();
    if (!trimmed) return false;

    const looksLikeDate =
      /\d{4}-\d{2}-\d{2}/.test(trimmed) ||
      trimmed.includes('T') ||
      /\d{1,2} [A-Za-z]{3}, \d{4}/.test(trimmed) ||
      /\d{1,2}\/\d{1,2}\/\d{4}/.test(trimmed);

    if (!looksLikeDate) return false;

    const parsed = new Date(trimmed);
    return !Number.isNaN(parsed.getTime());
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return formatHumanReadableDate(dateString);
    } catch (e) {
      return dateString;
    }
  };

  // WhatsApp-style timestamp formatting
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      if (messageDate.getTime() === today.getTime()) {
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }

      return `${formatHumanReadableDate(date)} ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })}`;
    } catch (e) {
      return '';
    }
  };

  // WhatsApp-style date separator
  const formatDateSeparator = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      if (messageDate.getTime() === today.getTime()) {
        return 'Today';
      } else if (messageDate.getTime() === yesterday.getTime()) {
        return 'Yesterday';
      } else {
        return formatHumanReadableDate(date);
      }
    } catch (e) {
      return '';
    }
  };

  const formatTableCellValue = (value, key = '') => {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'number') {
      return value.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    }

    const keyLooksDateLike = /date|time|created_at|updated_at|timestamp/i.test(String(key));
    if ((keyLooksDateLike || isDateLikeValue(value)) && isDateLikeValue(value)) {
      return formatHumanReadableDate(value);
    }

    return String(value);
  };

  const formatChartLabel = (label) => {
    if (isDateLikeValue(label)) {
      return formatHumanReadableDate(label);
    }

    return String(label);
  };

  // Check if we should show date separator between messages
  const shouldShowDateSeparator = (currentMsg, previousMsg) => {
    if (!previousMsg || !currentMsg) return true;
    
    try {
      const currentDate = currentMsg.timestamp instanceof Date 
        ? currentMsg.timestamp 
        : new Date(currentMsg.timestamp);
      const previousDate = previousMsg.timestamp instanceof Date 
        ? previousMsg.timestamp 
        : new Date(previousMsg.timestamp);
      
      const currentDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
      const previousDay = new Date(previousDate.getFullYear(), previousDate.getMonth(), previousDate.getDate());
      
      return currentDay.getTime() !== previousDay.getTime();
    } catch (e) {
      return false;
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="px-6 pt-4 bg-gray-50">
        <PanelBreadcrumbs current="DB Agent Chat" setActivePage={setActivePage} />
      </div>
      <div className="flex flex-col bg-gray-50 relative" style={{ height: 'calc(100vh - 120px)' }}>
      {/* Pinned Reports Section */}
      <div 
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-b border-gray-200 bg-white transition-all ${
          isDraggingOver
            ? 'border-[#25D366] bg-[#25D366]/5'
            : ''
        }`}
      >
        {showPinnedReports ? (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Pinned Reports</h2>
              <button
                onClick={() => setShowPinnedReports(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {pinnedReports.length === 0 ? (
              <p className="text-sm text-gray-500">No pinned reports yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {pinnedReports.map((report) => (
                <div
                  key={report.id || report.chat_message_id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{getVisualizationIcon(report.visualization?.type)}</span>
                      <div className="flex-1">
                        <h3 className="font-medium text-sm text-gray-900 truncate">
                          {report.report_title || 'Untitled Report'}
                        </h3>
                        {report.created_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(report.created_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {report.description && (
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                      {report.description}
                    </p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => {
                        if (setActivePage) {
                          // Store the report ID to auto-view it on the Pinned Reports page
                          sessionStorage.setItem('viewPinnedReportId', report.id.toString());
                          setActivePage('pinned-reports');
                        } else {
                          viewPinnedReport(report);
                        }
                      }}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-colors"
                    >
                      View
                    </button>
                    {report.csv_file_name && (
                      <button
                        onClick={async () => {
                          try {
                            const authData = localStorage.getItem('superadmin_auth');
                            const token = authData ? JSON.parse(authData).token : null;
                            const response = await axios.get(
                              `${API_BASE_URL}/api/pinned-reports/${report.id}`,
                              {
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                }
                              }
                            );
                            const reportData = response.data;
                            if (reportData.data && Array.isArray(reportData.data)) {
                              downloadCSV(reportData.data, report.csv_file_name, reportData.visualization);
                            }
                          } catch (error) {
                            console.error('Error downloading CSV:', error);
                            setToast({ message: 'Failed to download CSV', type: 'error' });
                          }
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                        title="Download CSV"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => unpinReport(report.id)}
                      className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors"
                      title="Remove Pin"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 py-3">
            <button
              onClick={() => {
                setShowPinnedReports(!showPinnedReports);
                if (!showPinnedReports) {
                  fetchPinnedReports();
                }
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors w-full"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <span className="flex-1 text-left">Pinned Reports {pinnedReports.length > 0 && `(${pinnedReports.length})`}</span>
              {isDraggingOver && (
                <span className="text-xs text-[#25D366] font-medium animate-pulse">Drop here to pin</span>
              )}
            </button>
          </div>
        )}
      </div>

      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-2 min-h-0 pb-24 relative bg-[#EFEAE2]"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'grid\' width=\'100\' height=\'100\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M 100 0 L 0 0 0 100\' fill=\'none\' stroke=\'%23e5e5e5\' stroke-width=\'0.5\' opacity=\'0.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100\' height=\'100\' fill=\'url(%23grid)\'/%3E%3C/svg%3E")' }}
      >

        {/* Intersection Observer Sentinel - invisible element at top */}
        <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
        
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <p>Ask your DB Agent anything! e.g., "Latest Laravel hosting options"</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const hasChart = msg.visualization && 
              typeof msg.visualization === 'object' && 
              Object.keys(msg.visualization).length > 0 &&
              msg.visualization.type && 
              msg.visualization.type !== 'table' && 
              msg.dataTable && 
              Array.isArray(msg.dataTable) && 
              msg.dataTable.length > 0;
            
            const hasTable = msg.dataTable && Array.isArray(msg.dataTable) && msg.dataTable.length > 0;
            const hasBoth = hasChart && hasTable;
            
            // Default to 'chart' if both are available, otherwise use what's available
            const currentViewMode = messageViewMode[idx] || (hasChart ? 'chart' : 'table');
            const showChart = hasChart && (currentViewMode === 'chart' || !hasTable);
            const showTable = hasTable && (currentViewMode === 'table' || !hasChart);
            
            // Check if this message is already pinned
            // Only check chat_message_id, not the pinned report's id
            const isPinned = msg.chatMessageId && pinnedReports.some(
              report => report.chat_message_id === msg.chatMessageId
            );
            
            const previousMsg = idx > 0 ? messages[idx - 1] : null;
            const showDateSeparator = shouldShowDateSeparator(msg, previousMsg);
            
            return (
              <React.Fragment key={idx}>
                {/* Date Separator */}
                {showDateSeparator && (
                  <div className="flex justify-center my-4">
                    <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                      {formatDateSeparator(msg.timestamp)}
                    </div>
                  </div>
                )}
                
                <div data-message-index={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} mb-1`}>
                  <div 
                    draggable={msg.role === 'ai' && msg.chatMessageId && (msg.dataTable || msg.visualization) ? true : false}
                    onDragStart={(e) => handleDragStart(e, msg)}
                    onDragEnd={handleDragEnd}
                    className={`max-w-[80%] ${hasChart ? 'min-w-[50%]' : ''} rounded-2xl px-3 py-2 shadow-sm transition-all ${
                      msg.role === 'user' 
                        ? 'bg-[#DCF8C6] text-gray-900' 
                        : 'bg-white border border-gray-200 text-gray-900'
                    } ${
                      msg.role === 'ai' && msg.chatMessageId && (msg.dataTable || msg.visualization)
                        ? 'cursor-move hover:shadow-md hover:scale-[1.02]'
                        : ''
                    }`}
                    title={msg.role === 'ai' && msg.chatMessageId && (msg.dataTable || msg.visualization) ? 'Drag to pin this report' : ''}
                  >
                    <div className="text-sm whitespace-pre-wrap mb-1">
                      {typeof msg.content === 'string' ? msg.content : String(msg.content)}
                    </div>
                    
                    {/* Action buttons - Toggle, Pin, Download */}
                    {(hasChart || hasTable) && (
                      <div className="flex items-center justify-end gap-2 mt-2 mb-1">
                        {/* Toggle between Chart and Table - only show if both are available */}
                        {hasBoth && (
                          <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg p-0.5">
                            <button
                              onClick={() => setMessageViewMode(prev => ({ ...prev, [idx]: 'chart' }))}
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                currentViewMode === 'chart'
                                  ? 'bg-indigo-600 text-white'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                              title="View Chart"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => setMessageViewMode(prev => ({ ...prev, [idx]: 'table' }))}
                              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                currentViewMode === 'table'
                                  ? 'bg-indigo-600 text-white'
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                              title="View Table"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        )}
                        
                        {msg.role === 'ai' && msg.chatMessageId && (
                          <button
                            onClick={() => isPinned ? null : pinReport(msg.chatMessageId)}
                            disabled={isPinned}
                            className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                              isPinned
                                ? 'text-green-600 bg-green-50 border border-green-300 cursor-default'
                                : 'text-yellow-600 bg-white border border-yellow-300 hover:bg-yellow-50'
                            }`}
                            title={isPinned ? 'Report is pinned' : 'Pin Report'}
                          >
                            <svg className="w-4 h-4" fill={isPinned ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                              {isPinned ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                              )}
                            </svg>
                            {isPinned ? 'Pinned' : 'Pin Report'}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            const filename = msg.csvFileName || 'report_' + new Date().toISOString().split('T')[0];
                            downloadCSV(msg.dataTable, filename, msg.visualization);
                          }}
                          className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-indigo-600 bg-white border border-indigo-300 rounded-lg hover:bg-indigo-50 transition-colors"
                          title="Download as CSV"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download CSV
                        </button>
                      </div>
                    )}
                    
                    {/* Chart or Table View */}
                    {(showChart || showTable) && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-xl border">
                        <div className="font-medium text-sm text-gray-800 mb-3">
                          {showChart ? (msg.visualization?.title || 'Chart') : 'Data Summary'}
                        </div>
                        
                        {/* Show Chart or Table based on current view mode */}
                        {showChart && (
                          <div className="h-64">
                            {renderChart(msg.visualization, msg.dataTable)}
                          </div>
                        )}
                        
                        {showTable && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs text-gray-700 min-w-[200px]">
                        <thead>
                          <tr className="border-b bg-gray-100">
                            {(() => {
                              const keys = Object.keys(msg.dataTable[0]);
                              const orderedKeys = [];
                              
                              if (msg.visualization?.x_axis && keys.includes(msg.visualization.x_axis)) {
                                orderedKeys.push(msg.visualization.x_axis);
                              }
                              
                              keys.forEach(key => {
                                if (!orderedKeys.includes(key)) {
                                  orderedKeys.push(key);
                                }
                              });
                              
                              return orderedKeys.map((key, keyIdx) => (
                                <th 
                                  key={keyIdx} 
                                  className="text-left py-2 px-3 font-medium text-gray-900"
                                  style={{ minWidth: '100px' }}
                                >
                                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </th>
                              ));
                            })()}
                          </tr>
                        </thead>
                        <tbody>
                          {msg.dataTable.map((row, rowIdx) => {
                            const keys = Object.keys(row);
                            const orderedKeys = [];
                            
                            if (msg.visualization?.x_axis && keys.includes(msg.visualization.x_axis)) {
                              orderedKeys.push(msg.visualization.x_axis);
                            }
                            
                            keys.forEach(key => {
                              if (!orderedKeys.includes(key)) {
                                orderedKeys.push(key);
                              }
                            });
                            
                            return (
                              <tr key={rowIdx} className="border-b hover:bg-gray-50 last:border-b-0">
                                {orderedKeys.map((key, colIdx) => (
                                  <td 
                                    key={colIdx} 
                                    className="py-2 px-3 font-mono"
                                    style={{ minWidth: '100px' }}
                                  >
                                    {formatTableCellValue(row[key], key)}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {msg.summary && typeof msg.summary === 'object' && Object.keys(msg.summary).length > 0 && (
                      <div className="mt-4 p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-200">
                        <div className="font-medium text-sm text-gray-800 mb-3">Summary</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {Object.entries(msg.summary).map(([key, value]) => (
                            <div key={key} className="bg-white rounded-lg p-3 border border-gray-200">
                              <div className="text-xs text-gray-500 mb-1">
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </div>
                              <div className="text-lg font-semibold text-gray-900">
                                {formatTableCellValue(value, key)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* WhatsApp-style timestamp - at the bottom of message */}
                    <div className={`flex items-center justify-end gap-1 mt-2 ${msg.role === 'user' ? 'text-gray-600' : 'text-gray-500'}`}>
                      <span className="text-[10px] leading-none">
                        {formatMessageTime(msg.timestamp)}
                      </span>
                      {msg.role === 'user' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 px-4 py-2 rounded-2xl shadow-md">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                <span className="text-sm text-gray-500">DB Agent thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* WhatsApp-style Sticky Input Bar */}
      <div className="border-t border-gray-200 bg-[#F0F2F5] absolute bottom-0 left-0 right-0 z-10">
        <div className="p-3">
          <div className="flex items-end gap-2">
            <div className="flex-1 bg-white rounded-3xl border border-gray-300 px-4 py-2.5 flex items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  // Auto-resize textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyPress={handleKeyPress}
                placeholder="Type a message"
                rows="1"
                className="flex-1 resize-none border-0 focus:outline-none text-sm text-gray-900 placeholder-gray-500 max-h-[120px] overflow-y-auto"
                disabled={loading}
                style={{ minHeight: '24px' }}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="bg-[#25D366] hover:bg-[#20BA5A] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full p-3 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center min-w-[48px] min-h-[48px]"
              title="Send"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Chat;
